import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Character Select component for homepage
 * Displays a fullscreen overlay with Boys/Girls uniform selection
 * 
 * @extends Component
 */
export class CharacterSelect extends Component {
  requiredRefs = ['dialog'];
  connectedCallback() {
    super.connectedCallback();

    const isHomepage = document.querySelector('[data-template="index"]');
    const hasSeenToday = this.hasSeenCharacterSelectToday();

    if (isHomepage && !hasSeenToday && !window.Shopify?.designMode) {
      setTimeout(() => this.showDialog(), 100);
    }

    this.addEventListener('click', this.handleSelection.bind(this));
    this.setupHoverEffects();
  }
  
  /**
   * Setup hover effects for character cards
   */
  setupHoverEffects() {
    const cards = this.querySelectorAll('.character-select__card');

    cards.forEach(card => {
      const img = card.querySelector('img');
      if (!img || !img.dataset.gif || !img.dataset.png) return;

      card.addEventListener('mouseenter', () => {
        img.src = img.dataset.gif;
      });

      card.addEventListener('mouseleave', () => {
        img.src = img.dataset.png;
      });

      // For touch devices, add touch support
      card.addEventListener('touchstart', () => {
        img.src = img.dataset.gif;
      }, { passive: true });

      card.addEventListener('touchend', () => {
        setTimeout(() => {
          img.src = img.dataset.png;
        }, 300);
      }, { passive: true });
    });
  }

  /**
   * Check if user has already seen the character select today
   * @returns {boolean}
   */
  hasSeenCharacterSelectToday() {
    const lastSeen = localStorage.getItem('character-select-last-seen');
    if (!lastSeen) return false;

    const today = new Date().toDateString();
    return lastSeen === today;
  }

  /**
   * Mark that user has seen the character select today
   */
  markAsSeen() {
    const today = new Date().toDateString();
    localStorage.setItem('character-select-last-seen', today);
  }

  /**
   * Save the user's character selection
   * @param {string} selection - 'boys' or 'girls'
   */
  saveSelection(selection) {
    localStorage.setItem('character-selection', selection);

    // Dispatch custom event so header icon can update
    window.dispatchEvent(new CustomEvent('character-selected', {
      detail: { selection }
    }));
  }

  /**
   * Get the current character selection
   * @returns {string|null} 'boys', 'girls', or null if not set
   */
  static getSelection() {
    return localStorage.getItem('character-selection');
  }
  
  /**
   * Handle card selection
   * @param {MouseEvent} event
   */
  handleSelection(event) {
    const card = event.target.closest('.character-select__card');
    if (!card) return;

    const link = card.dataset.link;
    if (!link) return;

    // Determine which selection was made
    const selection = card.classList.contains('character-select__card--boys') ? 'boys' : 'girls';

    this.markAsSeen();
    this.saveSelection(selection);

    card.classList.add('character-select__card--selected');

    // Verify the selection was saved before redirecting
    // This ensures localStorage has fully persisted
    setTimeout(() => {
      const saved = localStorage.getItem('character-selection');
      if (saved !== selection) {
        localStorage.setItem('character-selection', selection);
      }
      window.location.href = link;
    }, 300);
  }
  
  /**
   * Shows the dialog
   */
  showDialog() {
    const { dialog } = this.refs;
    
    if (dialog.open) return;
    
    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add('character-select-active');
    
    dialog.showModal();
    
    setTimeout(() => {
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }
  
  /**
   * Closes the dialog
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    
    if (!dialog.open) return;
    
    this.removeEventListener('keydown', this.#handleKeyDown);
    
    dialog.classList.add('dialog-closing');
    
    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });
    
    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.classList.remove('character-select-active');
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
    
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.markAsSeen();
  };
  
  /**
   * Handle escape key
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };
}

if (!customElements.get('character-select')) {
  customElements.define('character-select', CharacterSelect);
}